/**
 * 12-hour time picker (hour / minute / AM–PM) that always renders in 12-hour
 * format regardless of the browser or OS locale — unlike native
 * `<input type="time">`, whose displayed format follows the user's system
 * settings and can show 24h even for Latin American users.
 *
 * Value and onChange use 24-hour "HH:mm" strings to match API/storage format.
 */

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0..59

function parse24h(value: string): { hour12: number; minute: number; ampm: 'AM' | 'PM' } {
  const [hStr, mStr] = value.split(':');
  const h = parseInt(hStr, 10) || 0;
  const minute = parseInt(mStr, 10) || 0;
  const ampm: 'AM' | 'PM' = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return { hour12, minute, ampm };
}

function to24h(hour12: number, minute: number, ampm: 'AM' | 'PM'): string {
  let h = hour12 % 12;
  if (ampm === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

const selectStyle: React.CSSProperties = {
  padding: '8px 6px',
  fontSize: '14px',
  color: 'var(--dark-teal)',
  borderColor: 'var(--border-color)',
  backgroundColor: 'white',
  height: '40px',
  borderRadius: '8px',
  borderWidth: '1px',
};

export function TimeInput12h({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { hour12, minute, ampm } = parse24h(value || '09:00');

  return (
    <div className="flex items-center gap-1">
      <select
        aria-label="Hora"
        value={hour12}
        onChange={e => onChange(to24h(Number(e.target.value), minute, ampm))}
        className="border"
        style={selectStyle}
      >
        {HOURS_12.map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span style={{ color: 'var(--muted-fg)' }}>:</span>
      <select
        aria-label="Minutos"
        value={minute}
        onChange={e => onChange(to24h(hour12, Number(e.target.value), ampm))}
        className="border"
        style={selectStyle}
      >
        {MINUTES.map(m => (
          <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
        ))}
      </select>
      <select
        aria-label="AM/PM"
        value={ampm}
        onChange={e => onChange(to24h(hour12, minute, e.target.value as 'AM' | 'PM'))}
        className="border"
        style={selectStyle}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
